// THE OWNER'S CONTACT INFO, HELD BY THIS TAB AND NOWHERE ELSE (WEB-PARITY-5, ruling D3: "name and address asked at
// export, as the app does").
//
// The app keeps one contact profile on the phone (`OwnerProfileStore`): asked on Review & Export ("Add your contact
// info", ReviewView.swift:556-585), drawn as the letterhead at export, and sent with every letter request after it.
// The website keeps no profile (Tyler approved the two shortened app sentences that say so, 2026-10-01). So the
// contact the owner gives in "Your contact info" is held in this tab's MEMORY:
//   - `/letter` sends it with the PDF and the emailed copy as the letterhead (`letterhead`, contract 2.23.0), which the
//     server draws and keeps nowhere;
//   - `/you` and a change request send it as the letter's `owner`, as the app sends its profile.
// A reload forgets it, as it forgets the signature (./signature.ts). The server still holds the owner each letter was
// QUEUED with (`review.queuedWith.owner`), which is what a letter's own letterhead prints without one.
//
// NOT IN BROWSER STORAGE, for the signature's reason: a name, a home address, an email and a phone number on a shared
// computer's disk. `test/parity5.test.tsx` checks neither storage holds anything new after the sheet is saved.

import { useSyncExternalStore } from "react";
import type { Letterhead } from "@contract";

/** The app's `OwnerProfile` fields (OwnerProfileStore.swift), as the owner typed them. */
export type HeldContact = Letterhead;

export const EMPTY_CONTACT: HeldContact = { name: "", mailingAddress: "", city: "", state: "", zip: "", email: "", phone: "" };

let held: HeldContact | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function heldContact(): HeldContact | null {
  return held;
}

export function holdContact(contact: HeldContact): void {
  held = { ...contact };
  notify();
}

/** Test-only, and a fresh tab: nothing held. */
export function forgetContact(): void {
  if (held === null) return;
  held = null;
  notify();
}

/** The contact this tab holds, re-read whenever the sheet saves. */
export function useContact(): HeldContact | null {
  return useSyncExternalStore(subscribe, heldContact, () => null);
}

const t = (s: string): string => s.trim();

/** `OwnerProfile.isMinimallyComplete` (OwnerProfileStore.swift:22) — a name and a mailing street — over what the owner
 *  typed in the sheet. The server answers the same rule over each letter's queued letterhead
 *  (`review.letterhead.minimallyComplete`, functions/src/v2/letter/pdf/layout.ts `isMinimallyComplete`); this reads the
 *  owner's own typing before any letter carries it (docs/THIN_CLIENT_DEBT.md, added at birth). */
export function contactIsComplete(c: HeldContact | null): boolean {
  return c !== null && t(c.name) !== "" && t(c.mailingAddress) !== "";
}

/** The owner block a letter request carries (`GenerateLetterQueueInput.owner`: one mailing line). The street, then
 *  "City, ST ZIP" as the app's letterhead joins them (`OwnerProfile.formattedAddress`), on one line. */
export function ownerOf(c: HeldContact): { name: string; mailingAddress: string; email?: string; phone?: string } {
  const cityState = [t(c.city), t(c.state)].filter((s) => s !== "").join(", ");
  const last = [cityState, t(c.zip)].filter((s) => s !== "").join(" ");
  const mailing = [t(c.mailingAddress), last].filter((s) => s !== "").join(", ");
  return {
    name: t(c.name),
    mailingAddress: mailing,
    ...(t(c.email) === "" ? {} : { email: t(c.email) }),
    ...(t(c.phone) === "" ? {} : { phone: t(c.phone) }),
  };
}

/** Apple's one-line address for a picked suggestion — "street, city, ST ZIP", the shape `oneLine` writes
 *  (services/addressSuggest.ts) — split back into the sheet's fields, as the app's `applyCompletion` fills them from a
 *  placemark (OwnerProfileEditor.swift:116-128). A line that is not that shape goes into the street, as the app's
 *  fallback does. */
export function fieldsOfOneLine(line: string): Pick<HeldContact, "mailingAddress" | "city" | "state" | "zip"> {
  const parts = line.split(",").map((p) => p.trim());
  const m = parts.length === 3 ? /^([A-Za-z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/.exec(parts[2]) : null;
  if (m === null || parts[0] === "" || parts[1] === "") return { mailingAddress: line.trim(), city: "", state: "", zip: "" };
  return { mailingAddress: parts[0], city: parts[1], state: m[1].toUpperCase(), zip: m[2] ?? "" };
}
